import React, { useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import RestService from '../Services/RestService';
import OKomLoading from '../Components/OsloKommuneUI/OKomLoading';
import OKomBreadcrumb from '../Components/OsloKommuneUI/OKomBreadcrumb';
import OKomCheckbox from '../Components/OsloKommuneUI/OKomCheckbox';
import OKomModal from '../Components/OsloKommuneUI/OKomModal';

const AGE_BANDS: { ages: number[]; background: string }[] = [
  { ages: [4, 5, 6],                 background: '#f5b7c0' },
  { ages: [7, 8, 9, 10],             background: '#f5d7dc' },
  { ages: [11, 12, 13],              background: '#bfdff0' },
  { ages: [14, 15, 16, 17, 18],      background: '#f6d2a3' },
];

const backgroundForAge = (age: number): string => {
  for (const band of AGE_BANDS) {
    if (band.ages.includes(age)) return band.background;
  }
  return '#fff';
};

// Team/group events: fixed age GROUPS (same every year), hardcoded instead of read from config.
// Each inner array is one combined group → one merged cell with an X. Adjacent groups still get
// a dividing line. Edit the groups here if they ever change.
const HARDCODED_GROUPS: Record<string, number[][]> = {
  stafett_100_x_4: [[11, 12], [13, 14], [15, 16], [17, 18]],   // அஞ்சல் ஓட்டம் (4x100 m)
  kulai_eduthal:   [[11, 12, 13, 14], [15, 16, 17, 18]],       // குளை எடுத்தல்
  kili_thattu:     [[13, 14, 15], [16, 17, 18]],               // கிளித்தட்டு
};

// Escape the few characters that are special in LaTeX (Tamil is left untouched).
const escapeLatex = (s: string): string =>
  (s ?? '')
    .replace(/\\/g, '\\textbackslash{}')
    .replace(/([&%$#_{}])/g, '\\$1')
    .replace(/~/g, '\\textasciitilde{}')
    .replace(/\^/g, '\\textasciicircum{}');

const MAX_YEAR = 2035;

interface AthleticsRegistrationConfigProps extends RouteComponentProps {
  // When rendered inside the Friidrett config tabs, the parent owns the breadcrumb.
  embedded?: boolean;
  // When provided, the loading state is reported up (so a parent can show the banner
  // e.g. above its tab strip) and this component suppresses its own inline banner.
  onLoadingChange?: (loading: boolean) => void;
}

const AthleticsRegistrationConfigView: React.FC<AthleticsRegistrationConfigProps> = ({ embedded, onLoadingChange }) => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear);
  const [events, setEvents] = useState<Api.AthleticsEventDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());
  const [seedDialogOpen, setSeedDialogOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [latexOpen, setLatexOpen] = useState(false);
  const [latexCode, setLatexCode] = useState('');

  const isReadOnly = year < currentYear;
  const allYears = Array.from(
    { length: Math.max(1, MAX_YEAR - currentYear + 1) },
    (_, i) => currentYear + i,
  );

  useEffect(() => {
    document.title = 'Konfigurasjon — Friidrettsregistrering';
  }, []);

  // Report loading up so a parent (e.g. the Friidrett config tabs) can render the banner itself.
  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  const loadEvents = async (forYear: number) => {
    setIsLoading(true);
    try {
      const res = await RestService.get<Api.AthleticsEventDto[]>(`/api/athleticsregistration/GetEvents?year=${forYear}`);
      setEvents(res ?? []);
    } catch (e) {
      toast.error(`Kunne ikke laste øvelser: ${e}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadEvents(year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const confirmSeedFromPreviousYear = async () => {
    if (isReadOnly) return;
    const prev = year - 1;
    try {
      setSeeding(true);
      await RestService.post<{}, { Success: boolean; Inserted: number }>(
        `/api/athleticsregistration/SeedFromPreviousYear?year=${year}`,
        {},
      );
      await loadEvents(year);
      toast.success(`Data fra ${prev} ble hentet inn for ${year}.`);
      setSeedDialogOpen(false);
    } catch (e) {
      toast.error(`Kunne ikke hente data: ${e}`);
    } finally {
      setSeeding(false);
    }
  };

  const ages = Array.from({ length: 15 }, (_, i) => i + 4);

  // Build a full-width LaTeX tabularx for the events × age-group matrix (Tamil, rotated column
  // headers, age-band row colours), generated from all loaded events for the selected year.
  // Team/group events are rendered as one merged cell over their (contiguous) age range.
  const generateLatexCode = () => {
    const evs = events;
    const N = evs.length;
    // 2 fixed columns (birth year, age) + one flexible centered column per event => fills \textwidth.
    const colspec = '|c|c|' + '>{\\centering\\arraybackslash}X|'.repeat(N);
    const colorDefs = [
      ...AGE_BANDS.map((b, i) => `\\definecolor{aband${i}}{HTML}{${b.background.replace('#', '').toUpperCase()}}`),
      `\\definecolor{ahdr}{HTML}{EEEEEE}`,
    ];
    const header = ['பிறந்த ஆண்டு', 'வயது', ...evs.map(e => e.NameTa)]
      .map(t => `\\rotatebox{90}{${escapeLatex(t)}}`)
      .join(' & ');

    // Hardcoded team events: each fixed group is one merged cell. Other events read from config.
    // grp[c][i] = group index for event c at age i (-1 = none); null for non-hardcoded events.
    const grp = evs.map(e => {
      const groups = HARDCODED_GROUPS[e.ReadableId];
      return groups ? ages.map(a => groups.findIndex(g => g.includes(a))) : null;
    });
    const elig = evs.map((e, c) => grp[c]
      ? grp[c]!.map(g => g >= 0)
      : ages.map(a => e.Ages.includes(a)));
    const isMerged = evs.map((e, c) => grp[c] !== null);

    const body: string[] = [];
    for (let i = 0; i < ages.length; i++) {
      const a = ages[i];
      const band = AGE_BANDS.findIndex(b => b.ages.includes(a));
      const rc = band >= 0 ? `\\rowcolor{aband${band}} ` : '';
      const cells = [String(year - a), String(a)];
      for (let c = 0; c < N; c++) {
        if (isMerged[c]) {
          const g = grp[c]![i];
          if (g >= 0 && (i === ages.length - 1 || grp[c]![i + 1] !== g)) {
            // end of a group → anchor the merged X in the LAST row with a
            // negative span so it draws *after* the upper rows' \rowcolor
            // backgrounds (otherwise colortbl paints over it and the X vanishes).
            let len = 1;
            while (i - len >= 0 && grp[c]![i - len] === g) len++;
            cells.push(`\\multirow{-${len}}{*}{X}`);
          } else {
            cells.push(''); // inside a group, or not in any group
          }
        } else {
          cells.push(elig[c][i] ? 'X' : '');
        }
      }
      body.push(`${rc}${cells.join(' & ')} \\\\`);

      // Horizontal rule between this row and the next. Skip the rule for merged columns that
      // continue into the next row (so the merged cell stays a single block).
      if (i === ages.length - 1) {
        body.push('\\hline');
      } else {
        const ruled = [true, true, ...Array.from({ length: N }, (_, c) => {
          if (!isMerged[c]) return true;
          const gi = grp[c]![i], gj = grp[c]![i + 1];
          return !(gi >= 0 && gi === gj); // suppress the rule only within the same group
        })];
        if (ruled.every(Boolean)) {
          body.push('\\hline');
        } else {
          // Partial rule: \cline gets painted over by the next row's \rowcolor (colortbl
          // z-order), so use \hhline (drawn on top). Draw a rule on *every* column for uniform
          // thickness + no white seams; suppressed (within-group) segments are coloured with the
          // next row's band colour so they blend into the merged cell instead of showing a line.
          const nextBand = AGE_BANDS.findIndex(b => b.ages.includes(ages[i + 1]));
          const blend = nextBand >= 0 ? `aband${nextBand}` : 'black';
          let arg = '|';
          for (const r of ruled) {
            arg += r ? '>{\\arrayrulecolor{black}}-' : `>{\\arrayrulecolor{${blend}}}-`;
            arg += '>{\\arrayrulecolor{black}}|';
          }
          body.push(`\\hhline{${arg}}`);
        }
      }
    }

    const code = [
      `% விளையாட்டுப்போட்டிகள் மற்றும் பிரிவுகள் — ${year}`,
      `% Krever i preamble: graphicx, array, tabularx, colortbl, multirow, hhline (alle finnes i sportsregler.tex).`,
      ...colorDefs,
      `{\\setlength{\\tabcolsep}{4pt}\\setlength{\\arrayrulewidth}{1pt}\\renewcommand{\\arraystretch}{1.3}\\footnotesize`,
      `\\begin{tabularx}{\\textwidth}{${colspec}}`,
      `\\hline`,
      `\\rowcolor{ahdr} ${header} \\\\ \\hline`,
      ...body,
      `\\end{tabularx}}`,
    ].join('\n');
    setLatexCode(code);
    setLatexOpen(true);
  };

  const copyLatex = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(latexCode).then(() => toast.success('Kopiert')).catch(() => {});
    } else {
      toast.info('Marker teksten og kopier manuelt.');
    }
  };

  const toggleCell = async (eventId: number, age: number, nextEligible: boolean) => {
    if (isReadOnly) return;
    const key = `${eventId}-${age}`;
    if (pendingKeys.has(key)) return;

    setEvents(prev => prev.map(e => {
      if (e.Id !== eventId) return e;
      const newAges = nextEligible
        ? Array.from(new Set([...e.Ages, age])).sort((a, b) => a - b)
        : e.Ages.filter(a => a !== age);
      return { ...e, Ages: newAges };
    }));
    setPendingKeys(prev => new Set(prev).add(key));

    try {
      await RestService.post<Api.SetEligibilityRequest, void>(
        '/api/athleticsregistration/SetEligibility',
        { Year: year, Age: age, AthleticsEventId: eventId, Eligible: nextEligible },
      );
      setLastSavedAt(new Date());
    } catch (e) {
      setEvents(prev => prev.map(ev => {
        if (ev.Id !== eventId) return ev;
        const rolled = !nextEligible
          ? Array.from(new Set([...ev.Ages, age])).sort((a, b) => a - b)
          : ev.Ages.filter(a => a !== age);
        return { ...ev, Ages: rolled };
      }));
      toast.error(`Kunne ikke lagre: ${e}`);
    } finally {
      setPendingKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const cellStyle: React.CSSProperties = { border: '1px solid #888', padding: '8px 6px', textAlign: 'center', fontSize: '.9rem', wordBreak: 'break-word' };
  const headStyle: React.CSSProperties = { ...cellStyle, background: '#eee', whiteSpace: 'normal' };
  const rowHeadStyle: React.CSSProperties = { ...cellStyle, background: '#eee', fontWeight: 600 };
  const checkboxCellStyle: React.CSSProperties = { ...cellStyle, padding: '8px 0' };

  return (
    <>
      {!embedded && <OKomBreadcrumb path='Konfigurasjon, Friidrettsregistrering'></OKomBreadcrumb>}
      {!onLoadingChange && isLoading && <OKomLoading text="Laster..." />}
      <div className="ods-grid ods-grid--gap ods-grid--center" style={{ maxWidth: '95rem' }}>
        <div className="ods-grid__column--12 ods-text--align-center">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', justifyContent: 'center', alignItems: 'center' }}>
            <div className="ods-select" style={{ width: '12rem', textAlign: 'left' }}>
              <div className="ods-select__select">
                <select value={year} onChange={e => setYear(parseInt(e.target.value, 10))}>
                  {allYears.map(y => (
                    <option key={y} value={y}>{y}{y === currentYear ? ' (i år)' : ''}</option>
                  ))}
                </select>
              </div>
            </div>
            {!isReadOnly && (
              <button
                type="button"
                className="ods-button ods-button--red"
                onClick={() => setSeedDialogOpen(true)}
              >
                <span className="ods-button__icon ods-button__icon--left ods-icon--warning-triangle" aria-hidden="true"></span>
                Hent data fra forrige år
              </button>
            )}
            <button
              type="button"
              className="ods-button ods-button--outline"
              onClick={generateLatexCode}
              disabled={events.length === 0}
            >
              Generer latex kode
            </button>
          </div>
          {isReadOnly && (
            <p style={{ marginTop: '.5rem', color: '#a04040' }}>
              Tidligere år kan ikke endres. Velg gjeldende eller fremtidig år for å redigere.
            </p>
          )}
          {lastSavedAt && (
            <p className="ods-text--align-center ods-margin-top-3" style={{ fontSize: '.875rem' }}>
              <span key={lastSavedAt.getTime()} className="okom-flash-saved">
                Sist lagret: {String(lastSavedAt.getHours()).padStart(2, '0')}:
                {String(lastSavedAt.getMinutes()).padStart(2, '0')}:
                {String(lastSavedAt.getSeconds()).padStart(2, '0')}
              </span>
            </p>
          )}
        </div>
        <div className="ods-grid__column--12">
          <table style={{ borderCollapse: 'collapse', margin: '0 auto', background: '#fff', tableLayout: 'fixed', width: '100%' }}>
            <thead>
              <tr>
                <th style={headStyle}>Fødselsår</th>
                <th style={headStyle}>Aldersgruppe</th>
                {events.map(e => (
                  <th key={e.Id} style={headStyle}>{e.NameNo}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ages.map(a => {
                const bg = backgroundForAge(a);
                return (
                  <tr key={a} style={{ background: bg }}>
                    <th style={{ ...rowHeadStyle, background: bg }}>{year - a}</th>
                    <th style={{ ...rowHeadStyle, background: bg }}>{a}</th>
                    {events.map(e => {
                      const eligible = e.Ages.includes(a);
                      return (
                        <td key={e.Id} style={checkboxCellStyle}>
                          <div style={{ display: 'flex', justifyContent: 'center', opacity: isReadOnly ? 0.55 : 1, pointerEvents: isReadOnly ? 'none' : 'auto' }}>
                            <OKomCheckbox
                              key={`${e.Id}-${a}-${eligible}-${year}`}
                              checked={eligible}
                              onChange={(c) => toggleCell(e.Id, a, c)}
                            />
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <OKomModal
        open={seedDialogOpen}
        onClose={() => { if (!seeding) setSeedDialogOpen(false); }}
        labelledBy="seed-dialog-heading"
      >
        <div className="ods-grid ods-grid--gap">
          <div className="ods-grid__column--12">
            <h3
              id="seed-dialog-heading"
              className="ods-text--size-hotel ods-text--size-foxtrot-breakpoint-medium ods-text--size-delta-breakpoint-large ods-margin-bottom-8"
            >
              Hent data fra forrige år
            </h3>
            <p>
              Vil du erstatte øvelsesoppsettet for <strong>{year}</strong> med data fra <strong>{year - 1}</strong>?
            </p>
            <p className="ods-margin-top-2">
              Alle eksisterende valg for {year} vil bli slettet og erstattet.
            </p>
          </div>
          <div className="ods-grid__column--12">
            <button
              type="button"
              className="ods-button ods-button--red ods-margin-right-3"
              onClick={confirmSeedFromPreviousYear}
              disabled={seeding}
            >
              <span className="ods-button__icon ods-button__icon--left ods-icon--warning-triangle" aria-hidden="true"></span>
              Hent data
            </button>
            <button
              type="button"
              className="ods-button ods-button--outline"
              onClick={() => setSeedDialogOpen(false)}
              disabled={seeding}
            >
              Avbryt
            </button>
          </div>
        </div>
      </OKomModal>
      <OKomModal
        open={latexOpen}
        onClose={() => setLatexOpen(false)}
        labelledBy="latex-dialog-heading"
      >
        <div className="ods-grid ods-grid--gap">
          <div className="ods-grid__column--12">
            <h3 id="latex-dialog-heading" className="ods-text--size-hotel ods-margin-bottom-4">
              LaTeX-kode for matrisen ({year})
            </h3>
            <p style={{ fontSize: '.875rem' }}>
              Kopier og lim inn i <code>latex dokumentet</code>
            </p>
            <textarea
              readOnly
              value={latexCode}
              onFocus={e => e.currentTarget.select()}
              style={{ width: '100%', height: '22rem', fontFamily: 'monospace', fontSize: '.78rem', whiteSpace: 'pre', overflow: 'auto' }}
            />
          </div>
          <div className="ods-grid__column--12">
            <button type="button" className="ods-button ods-margin-right-3" onClick={copyLatex}>Kopier</button>
            <button type="button" className="ods-button ods-button--outline" onClick={() => setLatexOpen(false)}>Lukk</button>
          </div>
        </div>
      </OKomModal>
    </>
  );
};

export default withRouter(AthleticsRegistrationConfigView);
